// property-validation-rules.service.ts
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { environment } from "src/environments/environment";
import {
  PropertyRuleField,
  PropertyRuleSection,
} from "../../pages/entity/config/properties/models/property-validation-rules";

@Injectable({ providedIn: "root" })
export class PropertyValidationRulesService {
  private rulesMap = new Map<string, PropertyRuleField>();

  private rulesMap$ = new BehaviorSubject<Map<string, PropertyRuleField>>(
    this.rulesMap,
  );

  // controla se a feature está ativa para a entidade
  private globalEnabled = false;
  private globalEnabled$ = new BehaviorSubject<boolean>(this.globalEnabled);

  private loaded = false;
  private load$: Observable<Map<string, PropertyRuleField>> | null = null;

  constructor(private http: HttpClient) {}

  loadPropertyValidationRules(): Observable<Map<string, PropertyRuleField>> {
    if (this.loaded) return of(this.rulesMap);

    if (!this.load$) {
      this.load$ = this.http
        .get<any>(`${environment.apiUrl}/entities/property-validation-rules`, {
          withCredentials: true,
        })
        .pipe(
          map((data) => {
            const enabled = !!data?.entity?.propertyValidationRules;
            const sections = (data?.sections ?? []) as PropertyRuleSection[];

            return { enabled, sections };
          }),
          map(({ enabled, sections }) => {
            const mapRules = new Map<string, PropertyRuleField>();

            for (const section of sections) {
              for (const field of section.fields ?? []) {
                mapRules.set(field.key, field);
              }
            }
            return { enabled, mapRules };
          }),
          tap(({ enabled, mapRules }) => {
            this.globalEnabled = enabled;
            this.globalEnabled$.next(this.globalEnabled);

            this.rulesMap = mapRules;
            this.rulesMap$.next(this.rulesMap);

            this.loaded = true;
          }),
          map(({ mapRules }) => mapRules),
          shareReplay(1),
        );
    }

    return this.load$;
  }

  /** Recarrega ignorando cache */
  refreshPropertyValidationRules(): Observable<Map<string, PropertyRuleField>> {
    this.loaded = false;
    this.load$ = null;
    this.rulesMap = new Map();
    this.rulesMap$.next(this.rulesMap);

    return this.loadPropertyValidationRules();
  }

  /** Observable pra tabs reagirem quando carregar */
  rules$(): Observable<Map<string, PropertyRuleField>> {
    return this.rulesMap$.asObservable();
  }

  /** Observable pra UI saber se feature está ON */
  isGlobalEnabled$(): Observable<boolean> {
    return this.globalEnabled$.asObservable();
  }

  get(key: string): PropertyRuleField | undefined {
    return this.rulesMap.get(key);
  }

  isEnabled(key: string): boolean {
    // ✅ se feature OFF, nunca valida warnings
    // if (!this.globalEnabled) return false;

    return !!this.rulesMap.get(key)?.enabled;
  }

  minValue(key: string): number {
    const r = this.rulesMap.get(key);
    if (!r) return 1;
    const v = r.valueMin ?? r.defaultMin ?? 1;
    return Number(v) >= 1 ? Number(v) : 1;
  }

  /** Validator condicional */
  warningIfEnabled(
    ruleKey: string,
    predicate: (control: AbstractControl) => boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.isEnabled(ruleKey)) return null;

      return predicate(control) ? { warning: true } : null;
    };
  }
}
